/**
 * Financial types for HiponHero Web Dashboard
 *
 * The API returns decimal values as strings (Pydantic Decimal serialization).
 * Use toNumber() from @/lib/number for arithmetic operations.
 */

// ============================================================================
// Expense category
// ============================================================================

export interface ExpenseCategory {
  code: string;
  name: string;
  is_production_cost: boolean;
  auto_from_inventory: boolean;
  display_order: number;
}

// ============================================================================
// Expense types
// ============================================================================

export type ExpenseAllocationType = 'cycle' | 'pond' | 'farm';

export interface ExpenseCreate {
  category: string;
  amount: number;
  expense_date: string; // YYYY-MM-DD
  description?: string;
  allocation_type: ExpenseAllocationType;
  cycle_id?: string;
  pond_id?: string;
  vendor?: string;
  reference_number?: string;
  quantity?: number;
  unit?: string;
  unit_cost?: number;
  notes?: string;
}

export interface ExpenseUpdate {
  category?: string;
  amount?: number;
  expense_date?: string;
  description?: string;
  allocation_type?: ExpenseAllocationType;
  cycle_id?: string;
  pond_id?: string;
  vendor?: string;
  reference_number?: string;
  quantity?: number;
  unit?: string;
  unit_cost?: number;
  notes?: string;
}

export interface Expense {
  id: string;
  farm_id: string;
  cycle_id: string | null;
  pond_id: string | null;
  allocation_type: string;
  category: string;
  category_name: string | null;
  amount: number | string;
  expense_date: string;
  description: string | null;
  vendor: string | null;
  reference_number: string | null;
  quantity: number | string | null;
  unit: string | null;
  unit_cost: number | string | null;
  notes: string | null;
  created_by: string;
  created_by_name: string | null;
  cycle_name: string | null;
  created_at: string;
  last_modified: string;
}

// ============================================================================
// Adjustment types
// ============================================================================

export type AdjustmentType = 'increase' | 'decrease';

export interface AdjustmentCreate {
  adjustment_type: AdjustmentType;
  category?: string;
  amount: number;
  reason: string;
}

export interface Adjustment {
  id: string;
  farm_id: string;
  cycle_id: string;
  adjustment_type: string;
  category: string | null;
  category_name: string | null;
  amount: number | string;
  effective_amount: number | string | null;
  reason: string;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
}

// ============================================================================
// Cycle financials
// ============================================================================

export interface CostsBySource {
  inventory_costs: Record<string, number | string>;
  manual_expenses: Record<string, number | string>;
  adjustments_subtotal: number | string;
  total_cost: number | string;
  breakdown_pct: Record<string, number>;
}

export interface RevenueSummary {
  harvests: Array<{
    id: string;
    harvest_date: string;
    harvest_type: string;
    weight_kg: number | string;
    price_per_kg: number | string | null;
    revenue: number | string | null;
    size_grade: string | null;
    buyer: string | null;
  }>;
  total_weight_kg: number | string;
  total_revenue: number | string;
  avg_price_per_kg: number | string | null;
}

export interface ProfitabilitySummary {
  gross_profit: number | string;
  cost_per_kg: number | string | null;
  revenue_per_kg: number | string | null;
  profit_per_kg: number | string | null;
  roi_pct: number | null;
  status: 'profit' | 'loss' | 'no_revenue';
}

export interface CycleFinancials {
  cycle_id: string;
  cycle_name: string | null;
  pond_name: string | null;
  status: string | null;
  doc: number | null;
  stocking_date: string | null;
  costs: CostsBySource;
  revenue: RevenueSummary;
  profitability: ProfitabilitySummary;
  projections: {
    estimated_remaining_harvest_kg: number | string | null;
    breakeven_price_per_kg: number | string | null;
    projected_total_revenue_at_current_price: number | string | null;
    projected_profit_at_current_price: number | string | null;
  } | null;
}

// ============================================================================
// Cost breakdown
// ============================================================================

export interface CategoryBreakdown {
  category: string;
  category_name: string;
  source: string;
  amount: number | string;
  percentage: number;
  transactions: number;
}

export interface CostBreakdown {
  cycle_id: string;
  total_cost: number | string;
  by_category: CategoryBreakdown[];
}

// ============================================================================
// Farm summary
// ============================================================================

export interface CycleSummaryItem {
  cycle_id: string;
  cycle_name: string | null;
  pond_name: string | null;
  status: string | null;
  stocking_date: string | null;
  actual_harvest_date: string | null;
  total_cost: number | string;
  total_revenue: number | string;
  profit: number | string;
  harvest_kg: number | string;
  cost_per_kg: number | string | null;
  roi_pct: number | null;
}

export interface FarmFinancialSummary {
  farm_id: string;
  farm_name: string | null;
  period: Record<string, string> | null;
  total_cycles: number;
  harvested_cycles: number;
  active_cycles: number;
  total_costs: number | string;
  total_revenue: number | string;
  total_profit: number | string;
  total_harvest_kg: number | string;
  avg_cost_per_kg: number | string | null;
  avg_revenue_per_kg: number | string | null;
  avg_profit_per_kg: number | string | null;
  avg_roi_pct: number | null;
  by_cycle: CycleSummaryItem[];
  cost_breakdown: Record<string, number | string>;
}

// ============================================================================
// Comparison
// ============================================================================

export interface ComparisonCycleItem {
  cycle_id: string;
  cycle_name: string | null;
  pond_name: string | null;
  doc: number | null;
  status: string | null;
  total_cost: number | string;
  total_revenue: number | string;
  profit: number | string;
  harvest_kg: number | string;
  cost_per_kg: number | string | null;
  fcr: number | null;
  survival_pct: number | null;
}

export interface CycleComparison {
  cycles: ComparisonCycleItem[];
  best_performer: {
    lowest_cost_per_kg: string | null;
    highest_roi: string | null;
    highest_profit: string | null;
  } | null;
  insights: string[];
}

// ============================================================================
// Constants
// ============================================================================

export const ALLOCATION_TYPE_OPTIONS = [
  { value: 'cycle', label: 'Cycle' },
  { value: 'pond', label: 'Pond' },
  { value: 'farm', label: 'Farm-wide' },
] as const;

export const ADJUSTMENT_TYPE_OPTIONS = [
  { value: 'increase', label: 'Increase' },
  { value: 'decrease', label: 'Decrease' },
] as const;
